import { useEffect, useRef, useState } from 'react';
import { useNav } from '../app/nav.tsx';
import { useClients, type ClientRow } from '../lib/data.ts';
import { agoLabel, isoLocal, money, nowLocal } from '../lib/format.ts';
import { Avatar } from '../ui/bits.tsx';
import { HeaderWordmark } from '../ui/Header.tsx';
import { Failed, Skeleton } from '../ui/State.tsx';
import { Icon } from '../ui/Icon.tsx';

const FILTERS: Array<[string, string]> = [['all', 'Todas'], ['maintenance', 'Manutenção vencendo'], ['away', 'Sem vir há 45+ dias'], ['birthday', 'Aniversário no mês'], ['debt', 'Com pendência']];
type Sort = 'name' | 'last' | 'visits' | 'next';

// celular: lista · computador sem ficha aberta: tabela · computador com ficha aberta: lista estreita ao lado
export function Clientes({ mode = 'phone' }: { mode?: 'phone' | 'table' | 'list' }) {
  const nav = useNav();
  const [q, setQ] = useState('');
  const [f, setF] = useState(nav.params.filter ?? 'all');
  const [sort, setSort] = useState<{ by: Sort; dir: 1 | -1 }>({ by: 'name', dir: 1 });
  const search = useRef<HTMLInputElement>(null);
  const list = useClients(q, f);
  const rows = sortRows(list.data ?? [], sort);
  const open = (id: string) => nav.go('cliente', { id });
  // atalho "/": abre Clientes com a busca pronta para digitar
  useEffect(() => { if (nav.params.focus) search.current?.focus(); }, [nav.params.focus]);
  const today = nowLocal().date;
  const head = (by: Sort, label: string) => (
    <th aria-sort={sort.by === by ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button onClick={() => setSort(sort.by === by ? { by, dir: sort.dir === 1 ? -1 : 1 } : { by, dir: by === 'name' ? 1 : -1 })}>{label}{sort.by === by ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}</button>
    </th>
  );
  const empty = !rows.length && (q || f !== 'all');
  return (
    <div className={'view-enter cli-view' + (mode !== 'phone' ? ` cli-${mode}` : '')}>
      {mode === 'phone' && <HeaderWordmark />}
      <div className="heading cli-head" style={{ paddingTop: 6 }}>
        <div>{mode === 'phone' ? <><span>Clientes</span><h1>Quem já passou pela Sublime</h1></> : <h1>Clientes <small>{list.data ? `${rows.length}` : ''}</small></h1>}</div>
        <button className="cli-add" onClick={() => nav.open({ kind: 'editCli' })}><Icon name="plus" />Cliente</button>
      </div>
      <div className="cli-tools">
        <div className="search-in"><Icon name="search" />
          <input ref={search} type="search" placeholder="Buscar por nome ou telefone" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar cliente" /></div>
        {mode === 'list'
          ? <select className="cli-select" value={f} onChange={(e) => setF(e.target.value)} aria-label="Filtro">{FILTERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          : <div className="chips">{FILTERS.map(([k, l]) => <button key={k} className={f === k ? 'active' : ''} aria-pressed={f === k} onClick={() => setF(k)}>{l}</button>)}</div>}
      </div>
      {list.data === undefined ? (list.isError ? <Failed error={list.error} retry={() => list.refetch()} /> : <Skeleton rows={7} />) : !rows.length && !empty ? (
        <div className="empty-box"><b>Nenhuma cliente ainda</b><p>Cadastre quem já vem ao salão.</p>
          <div className="empty-acts"><button className="cta ghost" onClick={() => nav.open({ kind: 'editCli' })}>+ Cliente</button></div></div>
      ) : mode === 'table' ? (
        <div className="cli-table-wrap">
          <table className="cli-table">
            <thead><tr>{head('name', 'Nome')}<th>Celular</th>{head('last', 'Última visita')}{head('visits', 'Visitas')}{head('next', 'Próximo horário')}<th className="num">Saldo</th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} tabIndex={0} onClick={() => open(c.id)} onKeyDown={(e) => { if (e.key === 'Enter') open(c.id); }}>
                  <td><span className="cli-name"><Avatar id={c.id} name={c.name} /><b>{c.name}</b>{c.whatsapp ? null : <small title="Não autorizou mensagens no WhatsApp">sem WhatsApp</small>}</span></td>
                  <td className="mono">{c.phone || '—'}</td>
                  <td>{c.last_visit ? <>{isoLocal(c.last_visit).date.split('-').reverse().join('/')}<small>{agoLabel(c.days_away ?? 0)}{c.last_service ? ` · ${c.last_service}` : ''}</small></> : <span className="muted">ainda sem visita</span>}</td>
                  <td className="num">{c.visits}</td>
                  <td>{c.next_visit ? <>{nextLabel(c.next_visit, today)}</> : <span className="muted">—</span>}</td>
                  <td className="num">{c.debt > 0 ? <span className="late">deve {money(c.debt)}</span> : c.credit > 0 ? <span className="ok">crédito {money(c.credit)}</span> : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {empty && <div className="wempty">{q ? 'Ninguém com esse nome ou telefone.' : 'Ninguém neste filtro agora.'}</div>}
        </div>
      ) : (
        <div className="clist">
          <div className="gl cli-gl">
            {rows.map((c) => (
              <button key={c.id} className={'ccard cli-row' + (nav.params.id === c.id ? ' sel' : '')} aria-current={nav.params.id === c.id ? 'true' : undefined} onClick={() => open(c.id)}>
                <Avatar id={c.id} name={c.name} />
                <span className="main"><strong>{c.name}</strong><small>{c.last_visit ? `última vez ${agoLabel(c.days_away ?? 0)}` : 'ainda sem visita'}{c.debt > 0 ? ' · tem pendência' : ''}</small></span>
                {c.visits > 0 && <em className="cli-n">{c.visits} {c.visits === 1 ? 'visita' : 'visitas'}</em>}
              </button>
            ))}
          </div>
          {empty && <div className="wempty">{q ? 'Ninguém com esse nome ou telefone.' : 'Ninguém neste filtro agora.'}</div>}
        </div>
      )}
    </div>
  );
}

function nextLabel(iso: string, today: string): string {
  const l = isoLocal(iso);
  const d = l.date === today ? 'hoje' : l.date.split('-').reverse().slice(0, 2).join('/');
  return `${d} · ${l.time}`;
}

function sortRows(rows: ClientRow[], s: { by: Sort; dir: 1 | -1 }): ClientRow[] {
  const key = (c: ClientRow): string | number => s.by === 'name' ? c.name.toLowerCase() : s.by === 'visits' ? c.visits : s.by === 'last' ? (c.last_visit ?? '') : (c.next_visit ?? '');
  return [...rows].sort((a, b) => {
    const x = key(a), y = key(b);
    // sem data vai sempre para o fim
    if (x === '' && y !== '') return 1;
    if (y === '' && x !== '') return -1;
    return (x < y ? -1 : x > y ? 1 : 0) * s.dir || a.name.localeCompare(b.name);
  });
}
